/**
 * HICC Quick View — focus trap + page inert while modal is open.
 * Does not change open/close/load behaviour; only a11y layering.
 */
(function () {
  const QUICK_VIEW_ID = 'QuickView';
  const QV_MOBILE = '(max-width: 767px)';
  const FOCUSABLE =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const qvTrap = {
    active: false,
    didInitialFocus: false,
    keydown: null,
    focusin: null,
  };

  function getQuickViewRoot() {
    return document.getElementById(QUICK_VIEW_ID);
  }

  function isQuickViewOpen() {
    return Boolean(window.Alpine?.store('xQuickView')?.show);
  }

  function isQuickViewLoading() {
    return Boolean(window.Alpine?.store('xQuickView')?.loading);
  }

  function isQuickViewGalleryMobile() {
    return window.matchMedia(QV_MOBILE).matches;
  }

  function isQVSlideActive(slide) {
    if (!slide) return false;
    return slide.classList.contains('is-active') || slide.getAttribute('aria-hidden') !== 'true';
  }

  function syncQuickViewGalleryA11y() {
    if (!isQuickViewOpen()) return;
    const content = document.getElementById('quickview-product-content');
    if (!content) return;

    const isMobile = isQuickViewGalleryMobile();
    const thumbs = content.querySelector('[id^="x-product-thumbnail-"]');
    const pagination = content.querySelector('[id^="x-product-"] .splide__pagination');

    if (thumbs) {
      if (isMobile) {
        thumbs.setAttribute('aria-hidden', 'true');
        thumbs.setAttribute('inert', '');
      } else {
        thumbs.removeAttribute('aria-hidden');
        thumbs.removeAttribute('inert');
      }
    }

    if (pagination) {
      if (isMobile) {
        pagination.removeAttribute('aria-hidden');
        pagination.removeAttribute('inert');
      } else {
        pagination.setAttribute('aria-hidden', 'true');
        pagination.setAttribute('inert', '');
      }
    }

    content.querySelectorAll('[id^="x-product-"] .splide__slide').forEach((slide) => {
      const active = isQVSlideActive(slide);
      slide.querySelectorAll('button, a[href], input, select, textarea, [tabindex]').forEach((el) => {
        if (active) {
          el.removeAttribute('aria-hidden');
          if (el.dataset.hiccQvSavedTabindex != null) {
            el.setAttribute('tabindex', el.dataset.hiccQvSavedTabindex);
            delete el.dataset.hiccQvSavedTabindex;
          } else if (el.getAttribute('tabindex') === '-1' && el.matches('button[aria-label]')) {
            el.removeAttribute('tabindex');
          }
        } else if (!el.closest('[id^="x-product-thumbnail-"]')) {
          if (el.hasAttribute('tabindex') && el.getAttribute('tabindex') !== '-1') {
            el.dataset.hiccQvSavedTabindex = el.getAttribute('tabindex');
          }
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  function getQuickViewScrollport(fromEl) {
    const root = getQuickViewRoot();
    if (!root) return null;
    if (fromEl) {
      const scoped = fromEl.closest('.x-container-quickview-content');
      if (scoped && root.contains(scoped)) return scoped;
    }
    return root.querySelector('.x-container-quickview-content');
  }

  function scrollIntoQuickViewScrollport(el) {
    if (!el || !isQuickViewOpen()) return;

    const scrollport = getQuickViewScrollport(el);
    if (scrollport) {
      const elRect = el.getBoundingClientRect();
      const containerRect = scrollport.getBoundingClientRect();
      const padding = 12;

      if (elRect.top < containerRect.top + padding) {
        scrollport.scrollTop += elRect.top - containerRect.top - padding;
      } else if (elRect.bottom > containerRect.bottom - padding) {
        scrollport.scrollTop += elRect.bottom - containerRect.bottom + padding;
      }
    }

    const thumbScroller = el.closest('[id^="x-product-thumbnail-"]');
    if (thumbScroller) {
      const elRect = el.getBoundingClientRect();
      const scrollerRect = thumbScroller.getBoundingClientRect();
      const padding = 8;

      if (elRect.left < scrollerRect.left + padding) {
        thumbScroller.scrollLeft += elRect.left - scrollerRect.left - padding;
      } else if (elRect.right > scrollerRect.right - padding) {
        thumbScroller.scrollLeft += elRect.right - scrollerRect.right + padding;
      }
    }
  }

  function focusInQuickView(el) {
    if (!el) return;
    el.focus({ preventScroll: true });
    requestAnimationFrame(() => scrollIntoQuickViewScrollport(el));
  }

  function isElementVisible(el) {
    if (!el || el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return el.getClientRects().length > 0;
  }

  function isFocusableInTrap(el) {
    if (!el || el.disabled) return false;
    const content = document.getElementById('quickview-product-content');
    if (content?.contains(el)) {
      if (isQuickViewGalleryMobile() && el.closest('[id^="x-product-thumbnail-"]')) return false;
      if (!isQuickViewGalleryMobile() && el.closest('.splide__pagination')) return false;
      const slide = el.closest('.splide__slide');
      if (slide && !isQVSlideActive(slide)) return false;
    }
    if (el.matches('[data-pdp-purchase-mode][role="radio"]')) return isElementVisible(el);
    if (el.matches('input.input-radio, input[type="radio"]')) return true;
    if (el.matches('label[tabindex="0"]')) return isElementVisible(el);
    if (el.matches('shopify-payment-terms')) return true;
    return isElementVisible(el);
  }

  function getTrapFocusables(root) {
    if (!root) return [];
    const seen = new Set();
    const focusables = [];

    root.querySelectorAll(FOCUSABLE).forEach((el) => {
      if (!isFocusableInTrap(el) || seen.has(el)) return;
      seen.add(el);
      focusables.push(el);
    });

    return focusables;
  }

  function getActiveFocusableIndex(focusables, active) {
    let index = focusables.indexOf(active);
    if (index !== -1) return index;
    return focusables.findIndex((el) => el.contains(active));
  }

  function focusQuickViewTarget(focusTargetId, root) {
    const trapRoot = root || getQuickViewRoot();
    if (!trapRoot) return;
    const focusables = getTrapFocusables(trapRoot);
    const explicit = focusTargetId ? document.getElementById(focusTargetId) : null;
    const target =
      explicit && trapRoot.contains(explicit) ? explicit : focusables[0];
    focusInQuickView(target);
  }

  window.hiccSyncPageInert = function hiccSyncPageInert() {
    const main = document.getElementById('MainContent');
    if (!main || !window.Alpine) return;
    const cartOpen = Alpine.store('xMiniCart')?.open;
    const quickViewOpen = Alpine.store('xQuickView')?.show;
    main.inert = Boolean(cartOpen || quickViewOpen);
  };

  function clearQvTrapListeners() {
    if (qvTrap.keydown) document.removeEventListener('keydown', qvTrap.keydown, true);
    if (qvTrap.focusin) document.removeEventListener('focusin', qvTrap.focusin, true);
    qvTrap.keydown = null;
    qvTrap.focusin = null;
    qvTrap.active = false;
  }

  function releaseQvTrap() {
    clearQvTrapListeners();
    window.hiccSyncPageInert();
  }

  function moveQvFocus(focusables, delta) {
    if (!focusables.length) return;
    let activeIndex = getActiveFocusableIndex(focusables, document.activeElement);
    if (activeIndex === -1) activeIndex = 0;
    const nextIndex = (activeIndex + delta + focusables.length) % focusables.length;
    focusInQuickView(focusables[nextIndex]);
  }

  function attachQvFocusGuards() {
    qvTrap.focusin = (event) => {
      if (!isQuickViewOpen() || !qvTrap.keydown) return;
      const trapRoot = getQuickViewRoot();
      if (!trapRoot || trapRoot.contains(event.target)) return;
      event.stopImmediatePropagation();
      const focusables = getTrapFocusables(trapRoot);
      if (!focusables.length) return;
      const activeIndex = getActiveFocusableIndex(focusables, document.activeElement);
      focusInQuickView(activeIndex >= 0 ? focusables[activeIndex] : focusables[0]);
    };
    document.addEventListener('focusin', qvTrap.focusin, true);
    qvTrap.active = true;
  }

  function attachQvTabTrap() {
    qvTrap.keydown = (event) => {
      if (event.code !== 'Tab' || !isQuickViewOpen()) return;
      const trapRoot = getQuickViewRoot();
      if (!trapRoot) return;
      const focusables = getTrapFocusables(trapRoot);
      if (focusables.length < 2) return;

      event.preventDefault();
      moveQvFocus(focusables, event.shiftKey ? -1 : 1);
    };
    document.addEventListener('keydown', qvTrap.keydown, true);
  }

  function activateQvTrap(focusTargetId, options = {}) {
    const { initialFocus = false } = options;
    const root = getQuickViewRoot();
    if (!root || !isQuickViewOpen()) return;

    const focusInside = root.contains(document.activeElement);
    const shouldFocus = initialFocus && !focusInside;

    clearQvTrapListeners();
    window.hiccSyncPageInert();
    attachQvFocusGuards();

    if (!isQuickViewLoading()) {
      const focusables = getTrapFocusables(root);
      if (focusables.length >= 2) {
        attachQvTabTrap();
      }
    }

    if (shouldFocus) {
      focusQuickViewTarget(focusTargetId, root);
    }
  }

  function refreshQvTrap() {
    if (!isQuickViewOpen()) return;
    const root = getQuickViewRoot();
    const focusInside = root?.contains(document.activeElement);
    const shouldInitialFocus = !focusInside && !qvTrap.didInitialFocus;
    activateQvTrap('QuickViewClose', { initialFocus: shouldInitialFocus });
    if (shouldInitialFocus) {
      qvTrap.didInitialFocus = true;
    }
  }

  function scheduleQvTrapRefresh() {
    const run = () => {
      if (!isQuickViewOpen() || isQuickViewLoading()) return;
      syncQuickViewGalleryA11y();
      activateQvTrap('QuickViewClose', { initialFocus: false });
    };
    run();
    requestAnimationFrame(run);
    requestAnimationFrame(() => requestAnimationFrame(run));
    window.setTimeout(run, 50);
    window.setTimeout(run, 200);
  }

  function focusQuickViewTrigger() {
    const trigger = Alpine.store('xQuickView')?.buttonQuickView;
    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus({ preventScroll: true });
    }
  }

  function setupQuickViewScrollOnFocus() {
    if (window._hiccQvScrollOnFocus) return;
    window._hiccQvScrollOnFocus = true;

    document.addEventListener('focusin', (event) => {
      if (!isQuickViewOpen()) return;
      const root = getQuickViewRoot();
      if (!root?.contains(event.target)) return;
      requestAnimationFrame(() => scrollIntoQuickViewScrollport(event.target));
    });
  }

  function setupQuickViewGalleryA11y() {
    if (window._hiccQvGalleryA11yBound) return;
    window._hiccQvGalleryA11yBound = true;

    const bindSplide = () => {
      if (!isQuickViewOpen()) return;
      const content = document.getElementById('quickview-product-content');
      if (!content) return;
      const splide = content.querySelector('[id^="x-product-"]');
      if (!splide || splide.dataset.hiccQvGalleryMove) return;
      splide.dataset.hiccQvGalleryMove = 'true';
      splide.addEventListener('move', () => {
        syncQuickViewGalleryA11y();
        scheduleQvTrapRefresh();
      });
      syncQuickViewGalleryA11y();
    };

    document.addEventListener('quickview:content:loaded', bindSplide);
    window.addEventListener('resize', () => {
      syncQuickViewGalleryA11y();
      scheduleQvTrapRefresh();
    });
    bindSplide();
  }

  function setupQuickViewA11y() {
    if (!window.Alpine) return;

    const focusStore = Alpine.store('xFocusElement');
    if (focusStore && !focusStore._hiccQVTrapPatched) {
      const previousTrap = focusStore.trapFocus.bind(focusStore);
      const previousRemove = focusStore.removeTrapFocus.bind(focusStore);

      focusStore.trapFocus = function hiccQuickViewTrapFocus(containerId, elementFocusId) {
        if (containerId === QUICK_VIEW_ID) {
          refreshQvTrap();
          return;
        }
        releaseQvTrap();
        previousTrap(containerId, elementFocusId);
      };

      focusStore.removeTrapFocus = function hiccQuickViewRemoveTrapFocus(elementToFocus) {
        releaseQvTrap();
        previousRemove(elementToFocus || Alpine.store('xQuickView')?.buttonQuickView);
      };

      focusStore._hiccQVTrapPatched = true;
    }

    const quickView = Alpine.store('xQuickView');
    if (quickView && !quickView._hiccClosePatched) {
      const originalClose = quickView.close.bind(quickView);
      const originalRemoveFocus = quickView.removeFocusQuickView.bind(quickView);

      quickView.focusQuickView = function hiccFocusQuickView() {
        if (this.selected) return;
        refreshQvTrap();
      };

      quickView.close = function hiccQuickViewClose() {
        qvTrap.didInitialFocus = false;
        releaseQvTrap();
        originalClose();
        requestAnimationFrame(focusQuickViewTrigger);
      };

      quickView.removeFocusQuickView = function hiccRemoveFocusQuickView() {
        if (!this.selected) {
          releaseQvTrap();
          originalRemoveFocus();
          requestAnimationFrame(focusQuickViewTrigger);
        }
      };

      quickView._hiccClosePatched = true;
    }

    if (typeof Alpine.effect === 'function' && !window._hiccQuickViewTrapEffect) {
      Alpine.effect(() => {
        const qv = Alpine.store('xQuickView');
        if (!qv?.show) {
          qvTrap.didInitialFocus = false;
          releaseQvTrap();
          return;
        }

        window.hiccSyncPageInert();

        if (qv.loading) return;

        scheduleQvTrapRefresh();
      });
      window._hiccQuickViewTrapEffect = true;
    }

    setupQuickViewGalleryA11y();
    setupQuickViewScrollOnFocus();
  }

  document.addEventListener('quickview:content:loaded', scheduleQvTrapRefresh);

  document.addEventListener('alpine:init', setupQuickViewA11y);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupQuickViewA11y);
  } else {
    setupQuickViewA11y();
  }
})();
